import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Clock, MapPin, Navigation, User, Phone,
  AlertTriangle, CheckCircle2, ChevronRight, Sun, Search,
} from 'lucide-react';
import { useApp } from '@/context/AppContext';

const priorityConfig = {
  alta: { label: 'Urgente', dot: 'bg-destructive', ring: 'ring-destructive/20' },
  media: { label: 'Media', dot: 'bg-warning', ring: 'ring-warning/20' },
  bassa: { label: 'Bassa', dot: 'bg-success', ring: 'ring-success/20' },
};

const STATUS_PILLS = [
  { key: 'tutti', label: 'Tutti i pazienti' },
  { key: 'da_fare', label: 'Da fare' },
  { key: 'completati', label: 'Completati' },
];

const mockAgendaSlots = [
  { ora: '09:00', tipo: 'follow-up', completata: false },
  { ora: '10:30', tipo: 'prima-visita', completata: false },
  { ora: '11:00', tipo: 'prima-visita', completata: true },
  { ora: '14:00', tipo: 'follow-up', completata: false },
  { ora: '15:30', tipo: 'prima-visita', completata: true },
  { ora: '16:30', tipo: 'follow-up', completata: false },
];

export default function AgendaGiorno() {
  const { patients, showPendingNotifications } = useApp();
  const navigate = useNavigate();

  useEffect(() => { showPendingNotifications('medico'); }, [showPendingNotifications]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('tutti');

  const assignedPatients = useMemo(() => {
    return patients.filter(p => ['presa_in_carico', 'in_valutazione'].includes(p.stato));
  }, [patients]);

  const agenda = useMemo(() => {
    return mockAgendaSlots.map((slot, idx) => {
      const patient = assignedPatients[idx % assignedPatients.length];
      if (!patient) return null;
      return { ...slot, patient };
    }).filter(Boolean);
  }, [assignedPatients]);

  /* Filtering */
  const filteredAgenda = useMemo(() => {
    return agenda.filter(item => {
      const fullName = `${item.patient.cognome} ${item.patient.nome}`.toLowerCase();
      if (searchTerm && !fullName.includes(searchTerm.toLowerCase())) return false;
      if (statusFilter === 'da_fare' && item.completata) return false;
      if (statusFilter === 'completati' && !item.completata) return false;
      return true;
    });
  }, [agenda, searchTerm, statusFilter]);

  const oggi = new Date().toLocaleDateString('it-IT', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const completate = agenda.filter(a => a.completata).length;
  const totale = agenda.length;

  return (
    <AppLayout role="medico">
      <div className="space-y-6 max-w-3xl mx-auto">
        {/* Header con data */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sun className="w-5 h-5 text-warning" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Oggi</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground capitalize" data-testid="agenda-title">
              {oggi}
            </h1>
          </div>
          <Badge variant="chip" className="text-sm px-3 py-1.5 mt-1" data-testid="agenda-count">
            {completate}/{totale} visite
          </Badge>
        </div>

        {/* Summary pills */}
        <div className="flex gap-3">
          <div className="flex-1 bg-primary/8 border border-primary/12 rounded-xl px-4 py-3 text-center">
            <p className="text-2xl font-bold text-primary">{totale}</p>
            <p className="text-[11px] text-muted-foreground font-medium">Appuntamenti</p>
          </div>
          <div className="flex-1 bg-warning/8 border border-warning/12 rounded-xl px-4 py-3 text-center">
            <p className="text-2xl font-bold text-warning">{agenda.filter(a => a.patient.priorita === 'alta').length}</p>
            <p className="text-[11px] text-muted-foreground font-medium">Urgenti</p>
          </div>
          <div className="flex-1 bg-success/8 border border-success/12 rounded-xl px-4 py-3 text-center">
            <p className="text-2xl font-bold text-success">{completate}</p>
            <p className="text-[11px] text-muted-foreground font-medium">Completate</p>
          </div>
        </div>

        {/* ── Toolbar: Ricerca + Filtri Stato ── */}
        <div className="bg-card border border-border rounded-xl p-4 shadow-card space-y-3" data-testid="agenda-toolbar">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Cerca paziente per nome..."
              className="w-full h-10 rounded-lg border border-border bg-background text-foreground text-sm pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors"
              data-testid="agenda-search"
            />
          </div>
          <div className="flex gap-2" data-testid="agenda-status-filter">
            {STATUS_PILLS.map(pill => (
              <button
                key={pill.key}
                onClick={() => setStatusFilter(pill.key)}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                  statusFilter === pill.key
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
                data-testid={`status-pill-${pill.key}`}
              >
                {pill.label}
              </button>
            ))}
          </div>
        </div>

        {/* Appointment Cards */}
        <div className="flex flex-col gap-6" data-testid="agenda-list">
          {filteredAgenda.map((item, index) => {
            const pConf = priorityConfig[item.patient.priorita];
            const isRossi = item.patient.cognome === 'Rossi' && item.patient.nome === 'Maria';

            return (
              <motion.div
                key={`${item.ora}-${item.patient.id}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.06 }}
              >
                <Card
                  data-testid={`agenda-card-${item.patient.id}`}
                  className={`shadow-card transition-all duration-200 active:scale-[0.99] ${
                    item.completata ? 'opacity-60' : ''
                  } ${isRossi ? 'ring-2 ring-primary/30 border-primary/20' : ''}`}
                >
                  <CardContent className="p-0">
                    {/* Top strip: ora + priorita */}
                    <div className="flex items-center justify-between px-5 pt-4 pb-2">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-1.5">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="text-base font-bold text-foreground">{item.ora}</span>
                        </div>
                        <Badge variant="chip" className="text-[11px]">
                          {item.tipo === 'prima-visita' ? 'Prima Visita' : 'Follow-up'}
                        </Badge>
                        {item.completata && (
                          <Badge className="bg-success/10 text-success border-0 text-[10px] gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Completata
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`w-2.5 h-2.5 rounded-full ${pConf.dot}`} />
                        <span className="text-xs font-semibold text-muted-foreground">{pConf.label}</span>
                      </div>
                    </div>

                    {/* Patient info */}
                    <div className="px-5 pb-3">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <User className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-lg font-bold text-foreground leading-tight">
                            {item.patient.cognome} {item.patient.nome}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {item.patient.eta} anni &middot; {item.patient.diagnosiPrincipale.split('(')[0].trim()}
                          </p>
                        </div>
                      </div>

                      {/* Address */}
                      <div className="flex items-start gap-2 mb-3 ml-[60px]">
                        <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">{item.patient.indirizzo}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 px-5 pb-4">
                      <Button
                        variant="outline"
                        className="flex-1 gap-2 h-12 text-sm font-semibold"
                        data-testid={`nav-btn-${item.patient.id}`}
                      >
                        <Navigation className="w-4 h-4" />
                        Avvia Navigazione
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-12 w-12 shrink-0"
                        data-testid={`call-btn-${item.patient.id}`}
                      >
                        <Phone className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="comfort"
                        className="flex-[1.5] gap-2 h-12 text-sm font-semibold"
                        onClick={() => navigate(`/medico/valutazione/${item.patient.id}`)}
                        data-testid={`start-eval-btn-${item.patient.id}`}
                      >
                        Dettagli paziente
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Empty state */}
        {filteredAgenda.length === 0 && (
          <Card className="shadow-card">
            <CardContent className="p-12 text-center">
              {agenda.length === 0 ? (
                <>
                  <CheckCircle2 className="w-14 h-14 text-success mx-auto mb-4" />
                  <p className="text-lg font-semibold text-foreground">Nessun appuntamento oggi</p>
                  <p className="text-sm text-muted-foreground mt-1">La tua agenda e libera.</p>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-14 h-14 text-warning mx-auto mb-4" />
                  <p className="text-lg font-semibold text-foreground">Nessun risultato</p>
                  <p className="text-sm text-muted-foreground mt-1">Prova a modificare i filtri di ricerca.</p>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
